import type { SupabaseClient } from '@supabase/supabase-js';
import { getProdSupabaseClient } from './client';

// Cache tenant zip codes in memory (5 minute TTL)
// Invalidate on tenant/zone changes
const tenantZipCodeCache = new Map<string, { zipCodes: string[]; timestamp: number }>();
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

// Cache tenant type in memory (5 minute TTL)
const tenantTypeCache = new Map<string, { tenantType: string | null; timestamp: number }>();
const TENANT_TYPE_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Get prod client - singleton pattern ensures session is already hydrated
 * The prod client will automatically include Authorization header when making requests
 */
export async function getProdClientWithSession(): Promise<SupabaseClient> {
  // Singleton client is already created and hydrated at app initialization
  // Just return it - session is already in memory
  return getProdSupabaseClient();
}

/**
 * Get tenant zip codes with client-side caching
 * Returns array of zip codes for the given tenant
 */
export async function getTenantZipCodes(
  supabase: SupabaseClient,
  tenantId: string
): Promise<string[]> {
  // Check cache
  const cached = tenantZipCodeCache.get(tenantId);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.zipCodes;
  }

  // Fetch from database (returns SETOF, so we get an array)
  const { data, error } = await supabase.rpc('get_tenant_zip_codes', {
    p_tenant_id: tenantId
  });

  if (error) {
    // eslint-disable-next-line no-console
    console.error('Error fetching tenant zip codes:', error);
    throw error;
  }

  const zipCodes = data || [];

  // Update cache
  tenantZipCodeCache.set(tenantId, {
    zipCodes,
    timestamp: Date.now()
  });

  return zipCodes;
}

/**
 * Invalidate cache for a specific tenant
 * Call this when tenant zones are updated
 */
export function invalidateTenantZipCodeCache(tenantId: string): void {
  tenantZipCodeCache.delete(tenantId);
}

/**
 * Clear all cached tenant zip codes
 */
export function clearTenantZipCodeCache(): void {
  tenantZipCodeCache.clear();
}

/**
 * Check if tenant is internal type (e.g., Demo tenant)
 * Internal tenants bypass zone/zip code filtering and show latest snapshot date data
 */
export async function isInternalTenant(
  supabase: SupabaseClient,
  tenantId: string
): Promise<boolean> {
  if (!tenantId) {
    return false;
  }

  // Check cache
  const cached = tenantTypeCache.get(tenantId);
  if (cached && Date.now() - cached.timestamp < TENANT_TYPE_CACHE_TTL_MS) {
    return cached.tenantType === 'internal';
  }

  try {
    const { data, error } = await supabase
      .from('tenants')
      .select('tenant_type')
      .eq('id', tenantId)
      .single();

    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[isInternalTenant] Failed to check tenant type:', error);
      return false;
    }

    const tenantType = data?.tenant_type || null;

    // Update cache
    tenantTypeCache.set(tenantId, {
      tenantType,
      timestamp: Date.now()
    });

    return tenantType === 'internal';
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[isInternalTenant] Exception checking tenant type:', err);
    return false;
  }
}

/**
 * Invalidate tenant type cache for a specific tenant
 */
export function invalidateTenantTypeCache(tenantId: string): void {
  tenantTypeCache.delete(tenantId);
}

/**
 * Clear all cached tenant types
 */
export function clearTenantTypeCache(): void {
  tenantTypeCache.clear();
}

/**
 * Get tenant ID from JWT or session
 * Helper function to extract tenant_id from authentication context
 * Parses JWT token to get tenant_id claim (set by custom_access_token_hook)
 */
export async function getTenantIdFromSession(
  supabaseClient: SupabaseClient
): Promise<string | null> {
  try {
    const {
      data: { session }
    } = await supabaseClient.auth.getSession();

    if (!session?.access_token) {
      return null;
    }

    // Parse JWT to get claims (same approach as TenantContext)
    try {
      const payload = session.access_token.split('.')[1];
      if (!payload) {
        return null;
      }

      const claims = JSON.parse(atob(payload));
      if (claims.tenant_id) {
        return claims.tenant_id;
      }
    } catch {
      // JWT parsing failed
    }

    // Fallback: try user_metadata (legacy support)
    if (session.user?.user_metadata?.tenant_id) {
      return session.user.user_metadata.tenant_id;
    }

    // Final fallback: try app_metadata
    if (session.user?.app_metadata?.tenant_id) {
      return session.user.app_metadata.tenant_id;
    }

    return null;
  } catch {
    return null;
  }
}

/**
 * Get latest snapshot date from database
 */
export async function getLatestSnapshotDate(supabase: SupabaseClient): Promise<Date | null> {
  try {
    const { data, error } = await supabase.rpc('get_latest_snapshot_date');

    if (error) {
      // eslint-disable-next-line no-console
      console.warn('[getLatestSnapshotDate] RPC error:', error);
      return null;
    }

    return data ? new Date(data) : null;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[getLatestSnapshotDate] Exception:', err);
    return null;
  }
}

/**
 * Get recalls for tenant using RPC function
 * For internal tenants, uses get_recalls_for_viewport to bypass zone/zip filtering
 * This is the recommended approach for simple queries
 */
/* eslint-disable max-lines-per-function */
export async function getRecallsForTenant(
  supabase: SupabaseClient,
  tenantId: string,
  limit: number = 100,
  offset: number = 0
) {
  if (!tenantId) {
    /* eslint-disable-next-line no-console */
    console.error('[getRecallsForTenant] tenantId is required but was:', tenantId);
    return { data: null, error: { message: 'tenantId is required', code: 'INVALID_INPUT' } };
  }

  try {
    // Check if tenant is internal
    const tenantIsInternal = await isInternalTenant(supabase, tenantId);

    if (tenantIsInternal) {
      // For internal tenants, use get_recalls_for_viewport_latest_per_zone
      // Filters to latest snapshot_date per zone_slug (data collection zone)
      // Records with NULL zone_slug are from old data and are filtered out
      // Use viewport bounds (NULL) - caller will filter client-side if needed

      const { data, error } = await supabase.rpc('get_recalls_for_viewport_latest_per_zone', {
        p_south_lat: null, // No viewport bounds - get all data (caller will filter if needed)
        p_north_lat: null,
        p_west_lng: null,
        p_east_lng: null,
        p_snapshot_date: null, // NULL = compute latest date per zone_slug automatically
        p_limit: limit,
        p_offset: offset
      });

      if (error) {
        // If the RPC doesn't exist, fall back to get_recalls_for_viewport
        if (error.message?.includes('function') || error.code === '42883') {
          /* eslint-disable-next-line no-console */
          console.warn(
            '[getRecallsForTenant] get_recalls_for_viewport_latest_per_zone not found, ' +
              'falling back to get_recalls_for_viewport'
          );
          const fallbackResult = await supabase.rpc('get_recalls_for_viewport', {
            p_south_lat: null,
            p_north_lat: null,
            p_west_lng: null,
            p_east_lng: null,
            p_snapshot_date: null,
            p_limit: limit,
            p_offset: offset
          });

          return fallbackResult;
        }

        /* eslint-disable-next-line no-console */
        console.error('[getRecallsForTenant] RPC error (internal tenant):', {
          error,
          tenantId,
          limit,
          offset,
          errorCode: error.code,
          errorMessage: error.message
        });
      }

      return { data, error };
    } else {
      // For dealer tenants, use get_recalls_for_tenant (with zone/zip filtering)
      // The RPC function now filters to latest snapshot_date per zone_slug server-side
      const { data, error } = await supabase.rpc('get_recalls_for_tenant', {
        p_tenant_id: tenantId,
        p_limit: limit,
        p_offset: offset
      });

      if (error) {
        /* eslint-disable-next-line no-console */
        console.error('[getRecallsForTenant] RPC error:', {
          error,
          tenantId,
          limit,
          offset,
          errorCode: error.code,
          errorMessage: error.message,
          errorDetails: error.details,
          errorHint: error.hint
        });
      }

      return { data, error };
    }
  } catch (err: unknown) {
    /* eslint-disable-next-line no-console */
    console.error('[getRecallsForTenant] Exception:', {
      error: err,
      tenantId,
      limit,
      offset,
      message: (err as Error)?.message,
      stack: (err as Error)?.stack
    });
    const errorMessage = (err as Error)?.message || 'Unknown error';
    return { data: null, error: { message: errorMessage, code: 'EXCEPTION' } };
  }
}

/**
 * Get recalls for tenant with viewport bounds and filters
 * This supports map viewport queries
 */
/* eslint-disable max-lines-per-function, complexity */
export async function getRecallsForTenantWithFilters(
  supabase: SupabaseClient,
  tenantId: string,
  options: {
    southLat?: number;
    northLat?: number;
    westLng?: number;
    eastLng?: number;
    limit?: number;
    offset?: number;
    orderBy?: string;
    orderAsc?: boolean;
  } = {}
) {
  try {
    const { data, error } = await supabase.rpc('get_recalls_for_tenant_with_filters', {
      p_tenant_id: tenantId,
      p_south_lat: options.southLat ?? null,
      p_north_lat: options.northLat ?? null,
      p_west_lng: options.westLng ?? null,
      p_east_lng: options.eastLng ?? null,
      p_limit: options.limit ?? 1000,
      p_offset: options.offset ?? 0,
      p_order_by: options.orderBy ?? 'service_provider_name',
      p_order_asc: options.orderAsc ?? true
    });

    // If RPC function doesn't exist (404), return a clear error
    if (
      error &&
      (error.code === 'P0001' ||
        error.message?.includes('404') ||
        error.message?.includes('does not exist'))
    ) {
      return {
        data: null,
        error: {
          ...error,
          code: 'RPC_NOT_FOUND',
          message: 'get_recalls_for_tenant_with_filters RPC function not found in database'
        }
      };
    }

    // The RPC function now filters to latest snapshot_date per zone_slug server-side
    // No client-side filtering needed (already handled by RPC)
    return { data, error };
  } catch (err: unknown) {
    // Handle network errors or other exceptions
    const error = err as Error & { code?: string };
    if (
      error?.code === '404' ||
      error?.message?.includes('404') ||
      error?.message?.includes('Not Found')
    ) {
      return {
        data: null,
        error: {
          code: 'RPC_NOT_FOUND',
          message: 'get_recalls_for_tenant_with_filters RPC function not found in database',
          details: error
        }
      };
    }
    throw error;
  }
}

/**
 * Count recalls for tenant with optional viewport bounds
 */
export async function countRecallsForTenant(
  supabase: SupabaseClient,
  tenantId: string,
  options: {
    southLat?: number;
    northLat?: number;
    westLng?: number;
    eastLng?: number;
  } = {}
) {
  const { data, error } = await supabase.rpc('count_recalls_for_tenant', {
    p_tenant_id: tenantId,
    p_south_lat: options.southLat ?? null,
    p_north_lat: options.northLat ?? null,
    p_west_lng: options.westLng ?? null,
    p_east_lng: options.eastLng ?? null
  });

  return { data, error };
}

/**
 * Dealer aggregate data returned by getDealerAggregatesForTenant
 */
export interface DealerAggregate {
  dealer_key: string;
  dealer_name: string;
  dealer_lat: number;
  dealer_lon: number;
  dealer_address: string | null;
  dealer_city: string | null;
  dealer_state: string | null;
  dealer_zip: string | null;
  dealer_phone: string | null;
  recall_count: number;
  vehicle_count: number;
  campaign_count: number;
}

/**
 * Get pre-aggregated dealer data for fast map rendering (internal tenants only)
 *
 * This function returns dealer locations with recall/vehicle/campaign counts
 * instead of individual recall records. This reduces data transfer by ~100x
 * and eliminates the need for client-side grouping.
 *
 * For internal tenants (like Demo), this provides:
 * - ~593 dealer rows instead of ~4,151 recall rows for Atlanta
 * - ~50KB payload instead of ~2-5MB
 * - Instant map rendering without client-side processing
 *
 * Full recall details are loaded on-demand when a dealer is clicked.
 */
export async function getDealerAggregatesForTenant(
  supabase: SupabaseClient,
  tenantId: string,
  options: {
    southLat?: number;
    northLat?: number;
    westLng?: number;
    eastLng?: number;
    limit?: number;
  } = {}
): Promise<{ data: DealerAggregate[] | null; error: { message: string; code?: string } | null }> {
  if (!tenantId) {
    return { data: null, error: { message: 'tenantId is required', code: 'INVALID_INPUT' } };
  }

  try {
    // Only use aggregates for internal tenants
    const tenantIsInternal = await isInternalTenant(supabase, tenantId);

    if (!tenantIsInternal) {
      // For dealer tenants, return null to signal caller should use regular recalls
      return { data: null, error: { message: 'Not an internal tenant', code: 'NOT_INTERNAL' } };
    }

    const { data, error } = await supabase.rpc(
      'get_dealer_aggregates_for_viewport_latest_per_zone',
      {
        p_south_lat: options.southLat ?? null,
        p_north_lat: options.northLat ?? null,
        p_west_lng: options.westLng ?? null,
        p_east_lng: options.eastLng ?? null,
        p_limit: options.limit ?? 5000
      }
    );

    if (error) {
      // eslint-disable-next-line no-console
      console.error('[getDealerAggregatesForTenant] RPC error:', error);
      return { data: null, error: { message: error.message, code: error.code } };
    }

    return { data: data as DealerAggregate[], error: null };
  } catch (err: unknown) {
    const error = err as Error;
    // eslint-disable-next-line no-console
    console.error('[getDealerAggregatesForTenant] Exception:', error);
    return { data: null, error: { message: error.message || 'Unknown error', code: 'EXCEPTION' } };
  }
}
